import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '@auth/AuthProvider';
import TrainingController from '@data/controller/TrainingController';
import { TrainingModule, TrainingResource } from '@interfaces/training.interface';

export default function useTrainingModule(moduleID?: string, initialModule?: TrainingModule | null) {
  const auth = useAuth();
  const churchID = auth.churchId;

  const [module, setModule] = useState<TrainingModule | null>(initialModule ?? null);
  const [resources, setResources] = useState<TrainingResource[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!churchID || !moduleID) return;

    setLoading(true);
    setError(null);

    const response = await TrainingController.fetchTrainingModule(churchID, moduleID, auth);
    const result = response.body?.result;

    if (response.error || !result?.module) {
      setError(response.error || 'Failed to load this Training Module.');
      setModule(null);
      setResources([]);
    } else {
      setModule(result.module);
      setResources(result.resources ?? []);
    }

    setLoading(false);
    setHasLoaded(true);
  }, [churchID, moduleID, auth]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  return { module, resources, loading, hasLoaded, error, refetch: fetch } as const;
}
